/**
 * Learn center lessons (USA-1544). Each lesson is a short read, an optional practice run in
 * practice mode, and a short quiz. Progress is saved per person by lesson key, so never rename a
 * key once it has shipped.
 */

export type Block =
  | { kind: 'text'; text: string }
  | { kind: 'steps'; items: string[] }
  | { kind: 'image'; src: string; alt: string; caption: string; phone?: boolean }
  | { kind: 'rule'; title: string; text: string }
  | { kind: 'heading'; text: string }

export type PracticeStep = {
  text: string
  /** A link the step opens: a key of the recording's links (`po0`, `project`) or a dashboard path. */
  link?: string
  linkLabel?: string
  /** When the step is done; without one, the trainee taps Next. */
  done?: { visit: string } | { event: true } | { stage: number } | { write: true }
}

export type QuizQuestion = { question: string; choices: string[]; answer: number; why: string }

export type Lesson = {
  key: string
  title: string
  summary: string
  minutes: number
  body: Block[]
  /** `scenario` is a recorded practice world; null practises on real records with nothing saved. */
  practice?: { scenario: string | null; steps: PracticeStep[] }
  quiz?: QuizQuestion[]
}

export type Track = { key: string; title: string; description: string; lessons: string[] }

export const PASSING_SCORE = 80

export const LESSONS: Lesson[] = [
  {
    key: 'find-anything',
    title: 'Find anything in the dashboard',
    summary: 'Search, projects, POs and shipments: where each answer lives.',
    minutes: 4,
    body: [
      { kind: 'text', text: 'Almost every question starts with one record: a customer, a project, a PO or a shipment. Find it, and the rest is one click away.' },
      { kind: 'heading', text: 'Search first' },
      { kind: 'steps', items: [
        'Click Search at the top of any page, or press ⌘K (Ctrl K on Windows).',
        'Type a customer, project, PO number, order number or product.',
        'Pick the result to open it.',
      ] },
      { kind: 'heading', text: 'Where answers live' },
      { kind: 'rule', title: 'Project', text: "The whole job for one customer: its POs, shipments, invoices, emails and who's on it." },
      { kind: 'rule', title: 'Purchase order', text: 'One supplier order: the items, prices, the email thread with the supplier, the AI’s notes and the shipments.' },
      { kind: 'rule', title: 'Shipment', text: 'Carrier, tracking or PRO number, pickup and delivery dates.' },
      { kind: 'rule', title: 'Action items', text: 'What needs a person, sorted by role and priority. Projects → Action Items.' },
      { kind: 'text', text: 'Names in blue or underlined open their own page: a customer, a PO number, a manufacturer. Use My Projects / All in the header to switch between your projects and everyone’s.' },
    ],
    practice: {
      scenario: null,
      steps: [
        { text: 'Open Purchase Orders and pick any PO.', link: '/purchase-orders', linkLabel: 'Purchase Orders', done: { visit: '/purchase-orders/' } },
        { text: 'From the PO, open its project (the link above the PO number).', done: { visit: '/projects/' } },
        { text: 'Open Shipping → Shipments and pick any shipment to find its tracking.', link: '/shipments', linkLabel: 'Shipments', done: { visit: '/shipments/' } },
      ],
    },
    quiz: [
      {
        question: 'A customer asks about their order and gives you their PO number. Fastest way in?',
        choices: ['Scroll the Purchase Orders list', 'Type the number into Search (⌘K)', 'Ask Ops in Slack'],
        answer: 1,
        why: 'Search finds POs, projects, supplier invoices and customers by number or name from any page.',
      },
      {
        question: 'Where do you see the emails between us and a supplier about one order?',
        choices: ['On the purchase order', 'On the customer page', 'In Analytics'],
        answer: 0,
        why: 'Each PO keeps its supplier email thread, with the AI’s summary of each reply.',
      },
      {
        question: 'Which page shows everything for one customer job: all its POs, shipments and invoices?',
        choices: ['Sales Orders', 'The project', 'Shipping Calculator'],
        answer: 1,
        why: 'The project ties every PO, shipment and invoice for one customer job together.',
      },
    ],
  },
  {
    key: 'phone-alerts',
    title: 'Chat alerts on your phone',
    summary: 'Get a buzz when a shopper is waiting on you, on iPhone or Android.',
    minutes: 3,
    body: [
      { kind: 'text', text: 'When a shopper needs a person, your phone alerts you with their name and message. Tap it to open the chat. Set it up once on each phone.' },
      { kind: 'image', src: '/learn/alert-recreation.png', alt: 'A phone alert showing a shopper name and message', caption: 'What an alert looks like (a recreation; your phone draws it in its own style).' },
      { kind: 'rule', title: 'First', text: 'An admin turns on Answers store chats on your staff page (Admin → Staff). Without it there is no Alerts switch.' },
      { kind: 'image', src: '/learn/staff-switch.png', alt: 'Staff page with the Answers store chats switch on', caption: 'Answers store chats, on your staff page.' },
      { kind: 'heading', text: 'iPhone' },
      { kind: 'steps', items: [
        'Open Safari and go to dashboard.shopusars.com.',
        'Tap Share (the square with the arrow), then Add to Home Screen, then Add.',
        'Open USA-RS from your Home Screen and sign in with your Microsoft account.',
        'Tap ☰, then Customers → Chats.',
        'Flip the Alerts switch next to Chats, then tap Allow.',
      ] },
      { kind: 'image', src: '/learn/iphone-safari-hint.png', alt: 'Chats page in Safari with a Turn on alerts hint', caption: 'In Safari, Turn on alerts tells you to add it to the Home Screen first. iPhones need iOS 16.4 or newer.', phone: true },
      { kind: 'image', src: '/learn/iphone-app-on.png', alt: 'Chats page in the Home Screen app with alerts on', caption: 'In the Home Screen app: Alerts on.', phone: true },
      { kind: 'heading', text: 'Android' },
      { kind: 'steps', items: [
        'Open Chrome, go to dashboard.shopusars.com and sign in.',
        'Tap ☰, then Customers → Chats.',
        'Flip the Alerts switch, then tap Allow.',
        'Optional: ⋮ → Add to Home screen for an app icon.',
      ] },
      { kind: 'image', src: '/learn/android-chats-on.png', alt: 'Chats page on Android with alerts on', caption: 'Alerts on for this device.', phone: true },
      { kind: 'heading', text: 'If something’s off' },
      { kind: 'rule', title: 'No Alerts switch', text: 'Ask an admin to turn on Answers store chats for you, then fully close and reopen the app.' },
      { kind: 'rule', title: 'Alerts blocked', text: 'iPhone: Settings → Notifications → USA-RS → Allow Notifications. Android: tap the icon left of the address → Permissions → Notifications → Allow.' },
      { kind: 'rule', title: 'Another phone or computer', text: 'Turn Alerts on there too. Each device is switched on separately.' },
    ],
    quiz: [
      {
        question: 'On an iPhone, you see a Turn on alerts button instead of a switch. Why?',
        choices: ['Alerts are broken', "You're in Safari, not the Home Screen app", 'You need a new phone number'],
        answer: 1,
        why: 'iPhones only send alerts from the dashboard added to the Home Screen.',
      },
      {
        question: 'Which chats send you an alert?',
        choices: ['Every chat', 'Chats waiting on a person', 'Only chats from signed-in customers'],
        answer: 1,
        why: 'While the AI is answering there is no alert. You hear about chats that need a person, and chats you took over.',
      },
    ],
  },
  {
    key: 'action-items',
    title: 'Action items: what they are and how to close them',
    summary: 'What we must do (action items) and what others owe us (Waiting On).',
    minutes: 6,
    body: [
      { kind: 'text', text: 'Supplier, customer and carrier emails are read by the AI as they arrive. It handles what it can on its own: logging ship dates, tracking, small price changes. When something needs a person on our side, it raises an action item for the right role. If there’s no item, nobody does it, so items are how work gets done.' },
      { kind: 'heading', text: 'On every item' },
      { kind: 'rule', title: 'Choices', text: 'The AI suggests the real options ("Ship available items separately", "Hold the entire order"). Pick one, check the reply it drafted, and send it. Sending it closes the item once the email gives the supplier what the item asked of us.' },
      { kind: 'rule', title: 'Mark in progress', text: 'For our own work that takes time: arranging freight, getting a form signed, waiting on a call back. Add a note of what’s left so anyone can pick it up.' },
      { kind: 'rule', title: 'Comment', text: 'A note for the team, saved with the item.' },
      { kind: 'rule', title: 'Dismiss', text: 'Nothing needs doing. Add a short note why, so the next person knows.' },
      { kind: 'heading', text: 'When an item closes' },
      { kind: 'text', text: 'When our part is done: the answer, decision, document or payment delivered. If an email only does part of it (we passed on a contact, said we’re checking), the item stays open as In Progress.' },
      { kind: 'heading', text: 'Waiting On: what others owe us' },
      { kind: 'text', text: 'Action items track what we have to do. Waiting On tracks what suppliers, carriers and customers have to do for us: a ship date, tracking, a prepaid or corrected invoice, a credit, an account approval. Each has a due date.' },
      { kind: 'rule', title: 'Where it shows', text: 'Projects → Waiting On for everything, and the Waiting on panel on each PO and customer thread.' },
      { kind: 'rule', title: 'It chases itself', text: 'When something is late, automatic reminder emails go out first.' },
      { kind: 'rule', title: 'Then it asks you', text: 'If the reminders go unanswered, one item asks you to call them (“Call ProQuip: prepaid invoice”) with their number. Log the call with what they said and a new due date.' },
      { kind: 'rule', title: 'It closes itself', text: 'When it arrives (their email, the shipment, the invoice), it’s marked received on its own.' },
      { kind: 'heading', text: 'Your queue' },
      { kind: 'steps', items: [
        'Projects → Action Items lists open items by project. Filter by role, priority and staff.',
        'Highest priority first, then oldest.',
        'The Queue view walks you through them one at a time.',
      ] },
    ],
    practice: {
      scenario: null,
      steps: [
        { text: 'Open your action items.', link: '/projects/action-items', linkLabel: 'Action Items', done: { visit: '/projects/action-items' } },
        { text: 'Switch to the Queue view at the top and read the first item.' },
        { text: 'Open the purchase order behind an item and find the email the AI read.', done: { visit: '/purchase-orders/' } },
        { text: 'Open Projects → Waiting On and find what a supplier owes us and when it’s due.', link: '/projects/waiting-on', linkLabel: 'Waiting On', done: { visit: '/projects/waiting-on' } },
      ],
    },
    quiz: [
      {
        question: 'An item asks you to choose split shipment or hold. You pick “Ship available items separately” and send the AI’s drafted reply. What happens to the item?',
        choices: ['It closes: the email gave the supplier our decision', 'You mark it in progress until they ship', 'You resolve it by hand afterwards'],
        answer: 0,
        why: 'Sending the reply from the item delivers our decision, so the item closes. Their ship date and tracking are tracked under Waiting On.',
      },
      {
        question: 'When do you mark an item in progress?',
        choices: ['You emailed the supplier a question', 'You’ve started our own work that takes time, like arranging freight or getting a form signed', 'Never; items are only open or closed'],
        answer: 1,
        why: 'In Progress means someone on our side is working on it. Add a note of what’s left.',
      },
      {
        question: 'An item turns out to need nothing (the supplier already fixed it). Best move?',
        choices: ['Leave it open', 'Dismiss it with a short note why', 'Mark it in progress'],
        answer: 1,
        why: 'Dismiss with a note so the team can see why it was closed.',
      },
      {
        question: 'You’ve answered the supplier, and now they owe us a ship date. Where is that tracked?',
        choices: ['Waiting On', 'An open action item', 'Nowhere'],
        answer: 0,
        why: 'What the other side owes is on Waiting On, which follows up for you. Action items are for what we owe.',
      },
      {
        question: 'A supplier’s prepaid invoice is late and the reminder emails went unanswered. What happens?',
        choices: ['Nothing until someone notices', 'You get one item to call them, with their number', 'The PO is cancelled'],
        answer: 1,
        why: 'Waiting On emails first, then asks a person to call. Log the call with what they said and a new due date.',
      },
    ],
  },
  {
    key: 'ops-backorder',
    title: 'A supplier backorders part of a PO',
    summary: 'Read the AI’s summary, pick ship-now or hold, and send its reply.',
    minutes: 6,
    body: [
      { kind: 'text', text: 'Suppliers often ship what’s ready and backorder the rest. The AI logs the backorder date on the line, adds the expected shipments, and asks Ops to choose: ship the available items now, or hold everything for one shipment.' },
      { kind: 'rule', title: 'Ship now', text: 'Best when the customer needs the items on site, and the extra freight is small next to the order.' },
      { kind: 'rule', title: 'Hold', text: 'Best when the items are only useful together, or a second LTL shipment would cost a lot.' },
      { kind: 'text', text: 'Not sure what the customer wants? Request Customer Approval on the item drafts an email asking them. Check it and send it.' },
    ],
    practice: {
      scenario: 'supplier-backorder',
      steps: [
        { text: 'Open the practice PO. It went to the supplier and is waiting on their reply.', link: 'po0', linkLabel: 'Open the PO', done: { visit: 'po0' } },
        { text: 'Have the supplier reply. The AI reads it like a real email.', done: { event: true } },
        { text: 'Read the supplier’s email in the thread, then the AI’s action item about the backorder.' },
        { text: 'This customer wants items as soon as they’re ready. Pick the choice that ships the available items now, read the AI’s draft and send it.', done: { stage: 2 } },
        { text: 'Done. The item is resolved and the reply sits in the email thread.' },
      ],
    },
    quiz: [
      {
        question: 'The supplier says 2 of 3 items ship Monday; the third is out 6 weeks. What does the AI ask Ops?',
        choices: ['Whether to cancel the PO', 'Ship what’s ready now, or hold for one shipment', 'Nothing, it decides alone'],
        answer: 1,
        why: 'Splitting changes freight cost and what the customer gets when, so a person decides.',
      },
      {
        question: 'You’re not sure the customer wants a split delivery. What do you use?',
        choices: ['Request Customer Approval', 'Dismiss', 'Mark in progress and wait'],
        answer: 0,
        why: 'Request Customer Approval drafts the question to the customer right from the item.',
      },
    ],
  },
  {
    key: 'ops-price-increase',
    title: 'A supplier raises a price',
    summary: 'When the AI accepts a price change on its own, and when you decide.',
    minutes: 4,
    body: [
      { kind: 'text', text: 'When a supplier quotes a new price on a PO, the AI checks it against our rules.' },
      { kind: 'rule', title: 'The AI accepts it', text: 'The new price is the same or lower, or the increase adds less than $100 to the PO. It updates the line, makes a new PO PDF and sends it to the supplier.' },
      { kind: 'rule', title: 'You decide', text: 'Bigger increases and unexpected charges become an action item for Ops.' },
      { kind: 'text', text: 'Every change the AI makes shows on the PO: the line price, the PO history, and its reply in the email thread.' },
    ],
    practice: {
      scenario: 'price-increase',
      steps: [
        { text: 'Open the practice PO and note the line prices and total.', link: 'po0', linkLabel: 'Open the PO', done: { visit: 'po0' } },
        { text: 'Have the supplier reply with a new price.', done: { event: true } },
        { text: 'Find what changed: the line price, the new total, and the AI’s reply with the updated PO in the email thread.' },
        { text: 'Was this one under $100 or did it need you? Check the actions on the PO.' },
      ],
    },
    quiz: [
      {
        question: 'A supplier raises one line by $40. What happens?',
        choices: ['The AI accepts it and sends the updated PO', 'Ops gets an action item', 'The PO is cancelled'],
        answer: 0,
        why: 'Increases under $100 on the PO are accepted by the AI, which sends the supplier the updated PO.',
      },
      {
        question: 'A supplier adds a $450 increase. What happens?',
        choices: ['The AI accepts it', 'Ops gets an action item to decide', 'Nothing until the invoice'],
        answer: 1,
        why: 'Increases of $100 or more need a person.',
      },
    ],
  },
  {
    key: 'ops-tracking',
    title: 'Tracking arrives from a supplier',
    summary: 'See how a shipped email becomes a shipment with tracking.',
    minutes: 3,
    body: [
      { kind: 'text', text: 'When a supplier writes that the order shipped, the AI records the carrier, tracking or PRO number and dates on a shipment, and moves the PO to in transit. Nobody has to retype it.' },
      { kind: 'rule', title: 'Where to look', text: 'The PO’s Shipping section, the project, and Shipping → Shipments.' },
      { kind: 'rule', title: 'Customer asks where it is', text: 'Open the shipment: carrier, tracking and the estimated delivery date.' },
    ],
    practice: {
      scenario: 'tracking-arrives',
      steps: [
        { text: 'Open the practice PO. Nothing has shipped yet.', link: 'po0', linkLabel: 'Open the PO', done: { visit: 'po0' } },
        { text: 'Have the supplier send a shipped email.', done: { event: true } },
        { text: 'Find the new shipment on the PO, with its carrier and tracking number.' },
        { text: 'Open the shipment to see its delivery estimate.', done: { visit: '/shipments/' } },
      ],
    },
    quiz: [
      {
        question: 'A supplier emails a tracking number. What do you need to do?',
        choices: ['Type it into the shipment', 'Nothing, the AI records it; check it looks right', 'Forward it to the customer'],
        answer: 1,
        why: 'The AI records carrier, tracking and dates. Glance at the shipment to confirm.',
      },
    ],
  },
  {
    key: 'sales-order-status',
    title: 'Answer “where is my order?”',
    summary: 'What the AI answers on its own, and how to check and follow up.',
    minutes: 5,
    body: [
      { kind: 'text', text: 'When a customer emails about their order, the AI answers from the order itself: what’s ordered, shipped and expected, with a link to their order tracker. When it can’t confirm the dates, it drafts the reply and raises an action item for you to check and send.' },
      { kind: 'rule', title: 'The AI only acknowledges', text: 'Complaints, cancellations, refunds and escalations. It says we’re on it and raises an action item for a person.' },
      { kind: 'heading', text: 'When you answer yourself' },
      { kind: 'steps', items: [
        'Open the project (Search the customer or project name).',
        'Read each PO’s status: ordered, backordered, shipped, delivered.',
        'Open a shipped PO’s shipment for carrier, tracking and delivery date.',
        'Reply with what ships when, and when tracking will follow.',
      ] },
      { kind: 'rule', title: 'Good answer', text: '“Your ice machine shipped Tuesday, tracking 1Z…, arriving Friday. The range ships next week; we’ll send tracking when it leaves.”' },
      { kind: 'rule', title: 'Avoid', text: 'Promising a date the supplier hasn’t given. Say what we know and when we’ll know more.' },
    ],
    practice: {
      scenario: 'customer-status',
      steps: [
        { text: 'Open the customer’s email thread.', link: 'customer_thread', linkLabel: 'Open the thread', done: { visit: 'customer_thread' } },
        { text: 'Have the customer ask when their order arrives.', done: { event: true } },
        { text: 'Read the customer’s email, then the AI’s action item and its drafted reply.' },
        { text: 'Check the AI’s dates against the project’s POs.', link: 'project', linkLabel: 'Open the project', done: { visit: 'project' } },
        { text: 'Back in the thread, click Reply at the bottom and send the customer a short update.', link: 'customer_thread', linkLabel: 'Back to the thread', done: { stage: 2 } },
      ],
    },
    quiz: [
      {
        question: 'A customer emails “when will my order arrive?” What happens first?',
        choices: ['It waits for Sales', 'The AI answers from the order’s data, or drafts a reply for you to check', 'Nothing until they call'],
        answer: 1,
        why: 'The AI answers with the order’s own dates and a tracker link. When it can’t confirm them, it drafts the reply for a person.',
      },
      {
        question: 'A customer emails asking for a refund. What does the AI do?',
        choices: ['Refunds them', 'Acknowledges it and raises an action item for a person', 'Ignores it'],
        answer: 1,
        why: 'Refunds, cancellations and complaints always go to a person; the AI only acknowledges.',
      },
      {
        question: 'The supplier hasn’t given a ship date yet. Best reply?',
        choices: ['Give them a date anyway', 'Say it’s confirmed and we’ll send the ship date as soon as we have it', 'Don’t reply until there’s a date'],
        answer: 1,
        why: 'Share what we know and when we’ll know more. Never promise a date the supplier hasn’t given.',
      },
    ],
  },
  {
    key: 'store-chat',
    title: 'Answer store chats',
    summary: 'When the AI brings you in, how to take over, and how to hand back.',
    minutes: 5,
    body: [
      { kind: 'text', text: 'Shoppers chat from every page of shopusars.com. The AI answers first from our product data. When a chat needs a person, it says it’s checking with a manager and alerts the team.' },
      { kind: 'heading', text: 'The AI brings in a person for' },
      { kind: 'steps', items: [
        'A shopper asking for a person or a call',
        'Quote-sized orders: about $10,000+, a whole kitchen, several locations',
        'Discounts, faster shipping, waived freight, any exception to our terms',
        'Exact or rush dates',
        'Damage, missing items, returns, cancellations, billing',
      ] },
      { kind: 'heading', text: 'Answering' },
      { kind: 'rule', title: 'Customers → Chats', text: 'A red dot means a shopper is waiting on a person. Open the chat for the AI’s reason, the conversation, the page they’re on, their cart and our pricing.' },
      { kind: 'rule', title: 'Take over & send', text: 'Sends your reply and takes the chat from the AI. While you own it, the AI stays quiet.' },
      { kind: 'rule', title: 'Hand back to AI', text: 'When you’re done, hand it back in the dashboard so the shopper gets instant answers again.' },
      { kind: 'rule', title: 'Slack and email', text: 'Replying in the #storefront-chat thread or to the alert email also reaches the shopper. Handing back is dashboard only.' },
      { kind: 'text', text: 'Get alerts on your phone: see Chat alerts on your phone.' },
    ],
    practice: {
      scenario: null,
      steps: [
        { text: 'Open Customers → Chats.', link: '/customer-comms/chats', linkLabel: 'Chats', done: { visit: '/customer-comms/chats' } },
        { text: 'Open any chat and find the page the shopper was on, their cart and our pricing.', done: { visit: '/customer-comms/chats/' } },
        { text: 'Type a reply and press Take over & send. In practice mode it isn’t sent.', done: { write: true } },
      ],
    },
    quiz: [
      {
        question: 'A shopper asks for 10% off a $14,000 order. What does the AI do?',
        choices: ['Gives the discount', 'Says it’s checking with a manager and alerts the team', 'Refuses'],
        answer: 1,
        why: 'Discounts and quote-sized orders always come to a person.',
      },
      {
        question: 'You finished helping a shopper. What’s the last step?',
        choices: ['Close the tab', 'Hand back to AI in the dashboard', 'Reply in Slack'],
        answer: 1,
        why: 'Hand back so the AI answers their next question right away.',
      },
      {
        question: 'Can the AI share our Net or Profit with a shopper?',
        choices: ['Yes, if asked', 'Never; it doesn’t have them', 'Only to signed-in customers'],
        answer: 1,
        why: 'Net, Profit, Margin and suppliers are staff-only.',
      },
    ],
  },
  {
    key: 'accounting-basics',
    title: 'Supplier invoices and approvals',
    summary: 'Where supplier invoices land, and who approves them in what order.',
    minutes: 4,
    body: [
      { kind: 'text', text: 'Supplier invoices land in Accounting → AP Invoices, matched to their PO. Customer invoices are under AR Invoices.' },
      { kind: 'heading', text: 'Approvals, in order' },
      { kind: 'rule', title: 'Accounting', text: 'Checks every invoice first.' },
      { kind: 'rule', title: 'The project’s PM', text: 'Reviews it next only when the invoice is more than $5 off its PO.' },
      { kind: 'rule', title: 'Finance', text: 'Signs off on invoices of $10,000 or more, and on tax or missing-document questions.' },
      { kind: 'heading', text: 'Watch for' },
      { kind: 'rule', title: 'A mismatch', text: 'An action item asks someone to reconcile a price, fee, freight charge or total.' },
      { kind: 'rule', title: 'Same bill twice', text: 'A pro forma and the final invoice for the same order are one bill, not two.' },
      { kind: 'text', text: 'Open an invoice to see its lines next to the PO lines, its payments and its emails.' },
    ],
    practice: {
      scenario: null,
      steps: [
        { text: 'Open Accounting → AP Invoices.', link: '/invoices', linkLabel: 'AP Invoices', done: { visit: '/invoices' } },
        { text: 'Open any invoice and find the PO it belongs to.', done: { visit: '/invoices/' } },
        { text: 'Open the action items and filter the role to Accounting.', link: '/projects/action-items', linkLabel: 'Action Items', done: { visit: '/projects/action-items' } },
      ],
    },
    quiz: [
      {
        question: 'An invoice is $60 over its PO. Accounting approves it. Who looks next?',
        choices: ['Nobody, it’s paid', 'The project’s PM', 'The customer'],
        answer: 1,
        why: 'Invoices more than $5 off their PO go to the project’s PM after Accounting.',
      },
      {
        question: 'An invoice matches its PO to the cent and is $2,400. Who approves it?',
        choices: ['Accounting', 'Accounting, the PM and Finance'],
        answer: 0,
        why: 'Matching invoices under $10,000 only need Accounting.',
      },
      {
        question: 'A supplier sends a pro forma, then the final invoice for the same order. How many bills?',
        choices: ['One', 'Two'],
        answer: 0,
        why: 'They are the same bill; recording both would pay it twice.',
      },
    ],
  },
  {
    key: 'data-manufacturers',
    title: 'Update manufacturer data',
    summary: 'Terms, freight, contacts and lead times on the manufacturer page.',
    minutes: 4,
    body: [
      { kind: 'text', text: 'Each manufacturer page holds how we buy from them: discounts, freight program, minimums, lead times, contacts and who we order through.' },
      { kind: 'steps', items: [
        'Catalog → Manufacturers, then search the name.',
        'The top of the page shows what we buy at: best discount, freight, payment, lead time, minimum and where POs go.',
        'Open the source under Sourcing to edit its terms. Click a value, change it, press Enter.',
        'The pencil beside the address edits the address.',
      ] },
      { kind: 'rule', title: 'Changes reach pricing', text: 'Discounts and freight terms feed store prices and quotes, so double-check numbers before you leave the field.' },
      { kind: 'rule', title: 'Who we order through', text: 'Direct, a distributor, a rep group or an online retailer, set with the supplier picker.' },
    ],
    practice: {
      scenario: null,
      steps: [
        { text: 'Open Catalog → Manufacturers and pick any manufacturer.', link: '/manufacturers', linkLabel: 'Manufacturers', done: { visit: '/manufacturers/' } },
        { text: 'Find their freight program and lead time at the top of the page.' },
        { text: 'Click the pencil beside the address, change the suite line and save. In practice mode it stays in this tab only.', done: { write: true } },
      ],
    },
    quiz: [
      {
        question: 'Why double-check a discount before saving it?',
        choices: ['It feeds store prices and quotes', 'It emails the manufacturer', 'It can’t be changed later'],
        answer: 0,
        why: 'Discounts and freight terms flow into what customers pay.',
      },
    ],
  },
]

export const TRACKS: Track[] = [
  { key: 'everyone', title: 'Everyone', description: 'Start here', lessons: ['find-anything', 'action-items', 'phone-alerts'] },
  { key: 'ops', title: 'Operations', description: 'Suppliers, POs and shipping', lessons: ['action-items', 'ops-backorder', 'ops-price-increase', 'ops-tracking'] },
  { key: 'sales', title: 'Sales', description: 'Customers and orders', lessons: ['find-anything', 'sales-order-status', 'store-chat'] },
  { key: 'accounting', title: 'Accounting', description: 'Invoices and payments', lessons: ['find-anything', 'action-items', 'accounting-basics'] },
  { key: 'messaging', title: 'Online messaging', description: 'Store chats and customer email', lessons: ['store-chat', 'phone-alerts', 'sales-order-status'] },
  { key: 'data', title: 'Catalog data', description: 'Manufacturers and products', lessons: ['find-anything', 'data-manufacturers'] },
]

export const lessonByKey = (key: string) => LESSONS.find((lesson) => lesson.key === key)

/** The track that fits a staff member's role, if one does. */
export function trackForRole(role: string | null | undefined, answersStoreChats?: boolean | null) {
  const value = (role ?? '').toLowerCase()
  if (value.includes('sales')) return 'sales'
  if (value.includes('account') || value.includes('finance') || value.includes('ap_')) return 'accounting'
  if (value.includes('oper') || value.includes('project')) return 'ops'
  if (value.includes('data') || value.includes('catalog')) return 'data'
  if (answersStoreChats) return 'messaging'
  return 'everyone'
}
